import {
  Component,
  ChangeDetectionStrategy,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Loading } from '../../../../../utils/loading/loading';
import { SearchIcon } from '../../../../../shared/ui/icons/search-icon/search-icon';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import {
  PaymentRequestService,
  PaymentRequestCreatorAutoCompleteApiItem,
} from '../../../../../services/payment-request/payment-request.service';

@Component({
  selector: 'app-creator-auto-complete',
  imports: [CommonModule, FormsModule, Loading, SearchIcon, ErrorData],
  templateUrl: './creator-auto-complete.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorAutoComplete {
  private elementRef = inject(ElementRef);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  private paymentRequestService = inject(PaymentRequestService);

  isArabic = computed(() => this.locale.startsWith('ar'));

  label = input.required<string>();
  placeholder = input.required<string>();
  noResultsText = input.required<string>();

  minChars = input<number>(2);

  itemSelected = output<PaymentRequestCreatorAutoCompleteApiItem>();

  clear = input<number>(0);

  searchTerm = signal('');
  isOpen = signal(false);
  isLoading = signal(false);
  items = signal<PaymentRequestCreatorAutoCompleteApiItem[]>([]);
  selectedItem = signal<PaymentRequestCreatorAutoCompleteApiItem | null>(null);
  errorMessage = signal<string | null>(null);

  constructor() {
    effect(() => {
      this.clear();

      this.searchTerm.set('');
      this.items.set([]);
      this.isOpen.set(false);
      this.isLoading.set(false);
      this.errorMessage.set(null);
      this.selectedItem.set(null);
    });
  }

  onInput(value: string) {
    this.searchTerm.set(value);

    if (this.selectedItem()) {
      this.selectedItem.set(null);
    }

    const term = value.trim();

    if (term.length < this.minChars()) {
      this.items.set([]);
      this.isOpen.set(false);
      this.isLoading.set(false);
      this.errorMessage.set(null);
      return;
    }

    this.isOpen.set(true);
    this.isLoading.set(true);
    this.errorMessage.set(null);

    const subscription = this.paymentRequestService.getCreatorAutoComplete(term).subscribe({
      next: (response) => {
        this.items.set(response.list);
        this.isLoading.set(false);
        this.errorMessage.set(null);
      },

      error: (error) => {
        this.items.set([]);
        this.isLoading.set(false);
        const message = this.isArabic() ? error?.error?.messageAr : error?.error?.messageEn;

        this.errorMessage.set(
          message || (this.isArabic() ? 'حدث خطأ أثناء تحميل النتائج' : 'An error occurred while loading results'),
        );
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onFocus() {
    if (this.searchTerm().trim().length >= this.minChars() && !this.selectedItem() && this.items().length > 0) {
      this.isOpen.set(true);
    }
  }

  selectItem(item: PaymentRequestCreatorAutoCompleteApiItem) {
    this.selectedItem.set(item);
    this.searchTerm.set(item.fullName);
    this.itemSelected.emit(item);
    this.isOpen.set(false);
    this.isLoading.set(false);
  }

  get filteredItems(): PaymentRequestCreatorAutoCompleteApiItem[] {
    return this.items();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }
}